import { useState } from "react";
import { Loader2, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useTestSearch } from "@/hooks/use-knowledge-base";
import { failureMessage, type SearchedPassage } from "@/services/knowledge-base.service";
import { KB_MIN_SIMILARITY_DEFAULT } from "@/utils/kb-match-strictness";
import { KB_TOP_K_DEFAULT } from "@/utils/kb-results-per-search";

/** The function's longest question (knowledge-base QUERY_MAX_CHARS). */
export const QUERY_MAX_CHARS = 1000;

interface KbTestSearchProps {
  workspaceId: string;
  storeIds: string[];
  /** The agent's results per search and match strictness, saved or not; a knowledge base's own page has none: the defaults. */
  topK?: number;
  minSimilarity?: number;
}

/** Why a passage is or is not handed over, in the order the agent's search decides it. */
function verdictText(passage: SearchedPassage, topK: number, minSimilarity: number) {
  switch (passage.verdict) {
    case "sent":
      return "Sent to the agent";
    case "past_top_k":
      return `Not sent: past the ${topK} results per search`;
    case "below_cutoff":
      return `Not sent: not above the match strictness (${minSimilarity.toFixed(2)})`;
    default:
      return "Not sent: more than 0.05 below the best match";
  }
}

/**
 * The test console (owner, 2026-10-21): type a question, see the passages the agent's knowledge-base search would
 * hand over and the ones it would not, with their scores — the bot's own rule (shared/kb-search.json), run by the
 * knowledge-base function. On the agent editor it searches the agent's knowledge bases under its own settings, saved
 * or not; on a knowledge base's page, that knowledge base under the default ones.
 */
export function KbTestSearch({ workspaceId, storeIds, topK = KB_TOP_K_DEFAULT, minSimilarity = KB_MIN_SIMILARITY_DEFAULT }: KbTestSearchProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string>();
  const search = useTestSearch();
  const result = search.data;

  const run = () => {
    const question = query.trim();
    if (!question) return setError("Type a question first.");
    if (question.length > QUERY_MAX_CHARS) return setError(`Keep the question to ${QUERY_MAX_CHARS.toLocaleString("en-US")} characters.`);
    setError(undefined);
    search.mutate({ workspace_id: workspaceId, store_ids: storeIds, query: question, top_k: topK, min_similarity: minSimilarity });
  };

  return (
    <>
      <Button type="button" variant="outline" size="sm" disabled={storeIds.length === 0} onClick={() => setOpen(true)}>
        <Search className="mr-2 h-4 w-4" />
        Test a search
      </Button>
      <Dialog open={open} onOpenChange={() => !search.isPending && setOpen(false)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Test a search</DialogTitle>
            <DialogDescription>
              What the agent's knowledge-base search hands over for a question: up to {topK} results, each scoring above{" "}
              {minSimilarity.toFixed(2)} and within 0.05 of the best.
            </DialogDescription>
          </DialogHeader>

          <form
            className="space-y-2"
            onSubmit={(event) => {
              event.preventDefault();
              run();
            }}
          >
            <Label htmlFor="kb-test-query">Question</Label>
            <Textarea
              id="kb-test-query"
              rows={2}
              value={query}
              placeholder="What would a caller ask?"
              aria-invalid={!!error}
              aria-describedby={error ? "kb-test-query-error" : undefined}
              onChange={(event) => setQuery(event.target.value)}
            />
            {error && (
              <p id="kb-test-query-error" role="alert" className="text-xs text-destructive">
                {error}
              </p>
            )}
            <div className="flex justify-end">
              <Button type="submit" size="sm" disabled={search.isPending} aria-busy={search.isPending}>
                {search.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {search.isPending ? "Searching…" : "Search"}
              </Button>
            </div>
          </form>

          {search.isError && (
            <p role="alert" className="text-sm text-destructive">
              {failureMessage(search.error, "The search could not be run. Try again in a moment.")}
            </p>
          )}
          {result &&
            (result.passages.length === 0 ? (
              <p className="text-sm text-muted-foreground">No passage of a ready file matched. The agent gets nothing.</p>
            ) : (
              <ol aria-label="Passages found" className="max-h-[50vh] space-y-2 overflow-y-auto">
                {result.passages.map((passage) => (
                  <li key={passage.rank} data-testid="kb-test-passage" className="space-y-1 rounded-md border p-3">
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <Badge variant={passage.verdict === "sent" ? "default" : "secondary"}>
                        {verdictText(passage, result.top_k, result.min_similarity)}
                      </Badge>
                      <span className="font-medium">Score {passage.score.toFixed(3)}</span>
                      <span className="truncate text-muted-foreground" title={passage.file}>
                        {passage.file}
                      </span>
                    </div>
                    <p className="whitespace-pre-wrap break-words text-sm">{passage.content}</p>
                  </li>
                ))}
              </ol>
            ))}
        </DialogContent>
      </Dialog>
    </>
  );
}
